import React, { Component } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { Breadcrumb, Button, Card, Col, Menu, Result, Row, Space } from "antd";
import {
    DatabaseOutlined,
    FolderOutlined,
    HomeOutlined,
    PlaySquareOutlined
} from "@ant-design/icons";
import {
    fetchUserCreatedCollections,
    fetchUserCreatedModules,
    fetchUserCreatedVideos
} from "./../actions/contentCreation";
import MyCourses from "./MyCourses";
import MyModules from "./MyModules";
import MyVideos from "./MyVideos";
import CategorySubCategoryFilters from "./ContentFilters/CategorySubCategoryFilters";
import VideoTypeFilters from "./ContentFilters/VideoTypeFilters";

const TAB_KEYS = {
    COURSES: "courses",
    MODULES: "modules",
    VIDEOS: "videos"
};

const VALID_TABS = Object.values(TAB_KEYS);

const DEFAULT_FILTERS = {
    [TAB_KEYS.COURSES]: {
        category: null,
        subCategory: null
    },
    [TAB_KEYS.MODULES]: {
        category: null,
        subCategory: null
    },
    [TAB_KEYS.VIDEOS]: {
        types: []
    }
};

class MyContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeMenuItem: TAB_KEYS.COURSES,
            filters: DEFAULT_FILTERS
        };
    }

    getValidTabFromSearch = (search) => {
        const tab = new URLSearchParams(search).get("tab");
        return VALID_TABS.includes(tab) ? tab : null;
    };

    onMenuSelect = ({ key }) => {
        this.setState({ activeMenuItem: key });
        const params = new URLSearchParams(this.props.location.search);
        params.set("tab", key);
        this.props.history.replace({
            pathname: this.props.location.pathname,
            search: params.toString() ? `?${params.toString()}` : ""
        });
    };

    renderMenuLabel = (icon, label, count) => {
        return (
            <div className="my-content-menu-item">
                <span className="my-content-menu-icon">{icon}</span>
                <span className="my-content-menu-label">{label}</span>
                {count > 0 && <span className="my-content-menu-count bounce-in">{count}</span>}
            </div>
        );
    };

    getMenuInfoText = () => {
        if (this.state.activeMenuItem === TAB_KEYS.COURSES) {
            return "Your courses are reusable and can be managed, edited, and published from this workspace.";
        }
        if (this.state.activeMenuItem === TAB_KEYS.MODULES) {
            return "Your modules can be reused while creating collections and quickly assembled into new course structures.";
        }
        return "Your videos can be added while creating modules or collections, so build your video bank once and reuse it.";
    };

    getCategoryFromCollectionReducer = (categoryId) => {
        return (this.props.collectionCategories || []).find((c) => String(c.id) === String(categoryId));
    };

    getCategoryIdFromItem = (item) => {
        const rawCategory = item?.category?.id ?? item?.categoryId ?? item?.category ?? null;
        if (rawCategory === null || rawCategory === undefined || rawCategory === "") {
            const categoryName = item?.categoryName;
            if (!categoryName) {
                return null;
            }
            const matched = (this.props.collectionCategories || []).find(
                (category) =>
                    String(category?.name || "").toLowerCase() === String(categoryName).toLowerCase()
            );
            return matched?.id ?? null;
        }

        if (typeof rawCategory === "string") {
            if (/^\d+$/.test(rawCategory)) {
                return Number(rawCategory);
            }
            const matched = (this.props.collectionCategories || []).find(
                (category) =>
                    String(category?.name || "").toLowerCase() === rawCategory.toLowerCase()
            );
            if (matched?.id !== undefined) {
                return matched.id;
            }
        }

        return rawCategory;
    };

    getSubCategoryIdFromItem = (item) => {
        const rawSubCategory =
            item?.subCategory?.id ?? item?.subCategoryId ?? item?.subCategory ?? null;
        if (rawSubCategory === null || rawSubCategory === undefined || rawSubCategory === "") {
            const subCategoryName = item?.subCategoryName;
            const parentCategoryId = this.getCategoryIdFromItem(item);
            const category = this.getCategoryFromCollectionReducer(parentCategoryId);
            if (!subCategoryName) {
                return null;
            }

            if (category) {
                const matched = (category?.subCategories || []).find(
                    (subCategory) =>
                        String(subCategory?.name || "").toLowerCase() ===
                        String(subCategoryName).toLowerCase()
                );
                if (matched?.id !== undefined) {
                    return matched.id;
                }
            }

            // Fallback: find by sub-category name across all categories when parent is missing/null.
            for (const collectionCategory of this.props.collectionCategories || []) {
                const matched = (collectionCategory?.subCategories || []).find(
                    (subCategory) =>
                        String(subCategory?.name || "").toLowerCase() ===
                        String(subCategoryName).toLowerCase()
                );
                if (matched?.id !== undefined) {
                    return matched.id;
                }
            }
            return null;
        }

        if (typeof rawSubCategory === "string") {
            if (/^\d+$/.test(rawSubCategory)) {
                return Number(rawSubCategory);
            }
            const parentCategoryId = this.getCategoryIdFromItem(item);
            const category = this.getCategoryFromCollectionReducer(parentCategoryId);
            const matched = (category?.subCategories || []).find(
                (subCategory) =>
                    String(subCategory?.name || "").toLowerCase() ===
                    rawSubCategory.toLowerCase()
            );
            if (matched?.id !== undefined) {
                return matched.id;
            }

            // Fallback: name match globally
            for (const collectionCategory of this.props.collectionCategories || []) {
                const fallbackMatch = (collectionCategory?.subCategories || []).find(
                    (subCategory) =>
                        String(subCategory?.name || "").toLowerCase() ===
                        rawSubCategory.toLowerCase()
                );
                if (fallbackMatch?.id !== undefined) {
                    return fallbackMatch.id;
                }
            }
        }

        return rawSubCategory;
    };

    getCategoryNameById = (categoryId) => {
        const found = this.getCategoryFromCollectionReducer(categoryId);
        return found?.name || String(categoryId || "");
    };

    getSubCategoryNameById = (categoryId, subCategoryId) => {
        const category = this.getCategoryFromCollectionReducer(categoryId);
        const found = (category?.subCategories || []).find((s) => s.id === subCategoryId);
        return found?.name || String(subCategoryId || "");
    };

    getCategoryOptions = () => {
        return (this.props.collectionCategories || []).map((category) => ({
            label: category.name,
            value: category.id
        }));
    };

    getSubCategoryOptions = (categoryId) => {
        if (!categoryId) {
            return [];
        }
        const category = this.getCategoryFromCollectionReducer(categoryId);
        return (category?.subCategories || []).map((sub) => ({
            label: sub.name,
            value: sub.id
        }));
    };

    setCategoryFilter = (tabKey, categoryId) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                [tabKey]: {
                    ...prevState.filters[tabKey],
                    category: categoryId || null,
                    subCategory: null
                }
            }
        }));
    };

    setSubCategoryFilter = (tabKey, subCategoryId) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                [tabKey]: {
                    ...prevState.filters[tabKey],
                    subCategory: subCategoryId || null
                }
            }
        }));
    };

    setVideoTypeFilter = (types) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                videos: {
                    types: types || []
                }
            }
        }));
    };

    clearFilters = (tabKey) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                [tabKey]: DEFAULT_FILTERS[tabKey]
            }
        }));
    };

    getSelectedFilterTags = (tabKey) => {
        if (tabKey === TAB_KEYS.VIDEOS) {
            return this.state.filters[TAB_KEYS.VIDEOS].types.map((type) => ({
                key: `video-type-${type}`,
                label: type === "youtube" ? "YouTube" : "Vimeo",
                onClose: () => {
                    const nextTypes = this.state.filters[TAB_KEYS.VIDEOS].types.filter((v) => v !== type);
                    this.setVideoTypeFilter(nextTypes);
                }
            }));
        }

        const current = this.state.filters[tabKey];
        const tags = [];
        if (current.category) {
            tags.push({
                key: `${tabKey}-category-${current.category}`,
                label: `Category: ${this.getCategoryNameById(current.category)}`,
                onClose: () => this.setCategoryFilter(tabKey, null)
            });
        }
        if (current.subCategory) {
            tags.push({
                key: `${tabKey}-subcategory-${current.subCategory}`,
                label: `Sub Category: ${this.getSubCategoryNameById(current.category, current.subCategory)}`,
                onClose: () => this.setSubCategoryFilter(tabKey, null)
            });
        }
        return tags;
    };

    getFilterConfig = (tabKey) => {
        if (tabKey === TAB_KEYS.VIDEOS) {
            return {
                content: (
                    <VideoTypeFilters
                        selectedTypes={this.state.filters[TAB_KEYS.VIDEOS].types}
                        onTypesChange={this.setVideoTypeFilter}
                    />
                ),
                selectedTags: this.getSelectedFilterTags(TAB_KEYS.VIDEOS),
                onClear: () => this.clearFilters(TAB_KEYS.VIDEOS)
            };
        }

        const current = this.state.filters[tabKey];
        const subCategoryOptions = this.getSubCategoryOptions(current.category);

        return {
            content: (
                <CategorySubCategoryFilters
                    selectedCategory={current.category}
                    selectedSubCategory={current.subCategory}
                    categoryOptions={this.getCategoryOptions()}
                    subCategoryOptions={subCategoryOptions}
                    onCategoryChange={(value) => this.setCategoryFilter(tabKey, value)}
                    onSubCategoryChange={(value) => this.setSubCategoryFilter(tabKey, value)}
                />
            ),
            selectedTags: this.getSelectedFilterTags(tabKey),
            onClear: () => this.clearFilters(tabKey)
        };
    };

    filterByCategorySubCategory = (items, tabKey) => {
        const { category, subCategory } = this.state.filters[tabKey];
        return (items || []).filter((item) => {
            const itemCategoryId = this.getCategoryIdFromItem(item);
            const itemSubCategoryId = this.getSubCategoryIdFromItem(item);
            const categoryMatch = !category || String(itemCategoryId) === String(category);
            const subCategoryMatch = !subCategory || String(itemSubCategoryId) === String(subCategory);
            return categoryMatch && subCategoryMatch;
        });
    };

    getFilteredCollections = () => {
        return this.filterByCategorySubCategory(this.props.createdCollections, TAB_KEYS.COURSES);
    };

    getFilteredModules = () => {
        return this.filterByCategorySubCategory(this.props.createdModules, TAB_KEYS.MODULES);
    };

    getFilteredVideos = () => {
        const { types } = this.state.filters[TAB_KEYS.VIDEOS];
        if (!types.length) {
            return this.props.createdVideos || [];
        }
        return (this.props.createdVideos || []).filter((video) => types.includes(video?.videoType));
    };

    renderSelectedSection = () => {
        if (this.state.activeMenuItem === TAB_KEYS.COURSES) {
            return (
                <MyCourses
                    createdCollections={this.getFilteredCollections()}
                    loading={this.props.fetchingUserCreatedCollections}
                    filterConfig={this.getFilterConfig(TAB_KEYS.COURSES)}
                />
            );
        }
        if (this.state.activeMenuItem === TAB_KEYS.MODULES) {
            return (
                <MyModules
                    createdModules={this.getFilteredModules()}
                    loading={this.props.fetchingUserCreatedModules}
                    filterConfig={this.getFilterConfig(TAB_KEYS.MODULES)}
                />
            );
        }
        return (
            <MyVideos
                createdVideos={this.getFilteredVideos()}
                loading={this.props.fetchingUserCreatedVideos}
                filterConfig={this.getFilterConfig(TAB_KEYS.VIDEOS)}
            />
        );
    };

    componentDidMount() {
        const tabFromUrl = this.getValidTabFromSearch(this.props.location.search);
        if (tabFromUrl && tabFromUrl !== this.state.activeMenuItem) {
            this.setState({ activeMenuItem: tabFromUrl });
        }

        if (this.props.user) {
            this.props.fetchUserCreatedModules(this.props.user.id);
            this.props.fetchUserCreatedCollections(this.props.user.id);
            this.props.fetchUserCreatedVideos(this.props.user.id);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search !== this.props.location.search) {
            const tabFromUrl = this.getValidTabFromSearch(this.props.location.search);
            if (tabFromUrl && tabFromUrl !== this.state.activeMenuItem) {
                this.setState({ activeMenuItem: tabFromUrl });
            }
        }
    }

    render() {
        if (!this.props.user || !this.props.user.isContentCreator) {
            return (
                <Result
                    status="403"
                    title="403"
                    subTitle="Sorry, you are not authorized to access this page."
                    extra={
                        <Button
                            type="primary"
                            onClick={() => this.props.history.push("/")}>
                            Go Back to Home
                        </Button>
                    }
                />
            );
        }

        return (
            <div id="my-content-page">
                <Space className="ant-space-responsive flex space-between align-center">
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <Link to="/">
                                <HomeOutlined />
                            </Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>My Content</Breadcrumb.Item>
                    </Breadcrumb>
                </Space>

                <Card style={{ marginTop: "20px" }}>
                    <Row gutter={24}>
                        <Col
                            xs={24}
                            md={7}
                            lg={6}
                            style={{ borderRight: "1px solid #f0f0f0", minHeight: "calc(100vh - 200px)" }}>
                            <div className="my-content-menu-title">CONTENT</div>
                            <Menu
                                className="my-content-side-menu"
                                mode="inline"
                                selectedKeys={[this.state.activeMenuItem]}
                                onSelect={this.onMenuSelect}
                                items={[
                                    {
                                        key: "courses",
                                        label: this.renderMenuLabel(
                                            <FolderOutlined />,
                                            "My Courses",
                                            this.props.createdCollections.length
                                        )
                                    },
                                    {
                                        key: "modules",
                                        label: this.renderMenuLabel(
                                            <DatabaseOutlined />,
                                            "My Modules",
                                            this.props.createdModules.length
                                        )
                                    },
                                    {
                                        key: "videos",
                                        label: this.renderMenuLabel(
                                            <PlaySquareOutlined />,
                                            "My Videos",
                                            this.props.createdVideos.length
                                        )
                                    }
                                ]}
                            />
                            <div className="my-content-menu-info">
                                {this.getMenuInfoText()}
                            </div>
                        </Col>
                        <Col xs={24} md={17} lg={18}>
                            {this.renderSelectedSection()}
                        </Col>
                    </Row>
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        user: state.userReducer.user,
        isAuthenticated: state.userReducer.isAuthenticated,
        createdCollections: state.contentCreationReducer.userCreatedCollections,
        createdModules: state.contentCreationReducer.userCreatedModules,
        createdVideos: state.contentCreationReducer.userCreatedVideos,
        fetchingUserCreatedCollections: state.contentCreationReducer.fetchingUserCreatedCollections,
        fetchingUserCreatedModules: state.contentCreationReducer.fetchingUserCreatedModules,
        fetchingUserCreatedVideos: state.contentCreationReducer.fetchingUserCreatedVideos,
        collectionCategories: state.collectionReducer.collectionCategories
    };
};

export default connect(mapStateToProps, {
    fetchUserCreatedCollections,
    fetchUserCreatedModules,
    fetchUserCreatedVideos
})(MyContent);
